import { BaseComponent, Building } from "../types/protocols";

/**
 * A partial set of component fields that were persisted. A value of `null`
 * removes the field. Used to apply an edit to local state without refetching.
 */
export type ComponentFieldPatch = Record<string, unknown>;

/** The component's fields with the patch applied; `null`/`undefined` removes a field. */
export function applyComponentFieldPatch(
    component: object,
    patch: ComponentFieldPatch
): Record<string, unknown> {
    const updated: Record<string, unknown> = { ...component };
    for (const [field, value] of Object.entries(patch)) {
        if (value === null || value === undefined) {
            delete updated[field];
        } else {
            updated[field] = value;
        }
    }
    return updated;
}

/**
 * The building with one component patched. Returns the same building when it
 * has no such component, so a state setter can pass it straight through.
 */
export function applyComponentPatch(
    building: Building | null,
    componentKey: string,
    patch: ComponentFieldPatch
): Building | null {
    const components = building?.model.structure?.components;
    if (!building || !components || !components[componentKey]) return building;

    return {
        ...building,
        model: {
            ...building.model,
            structure: {
                ...building.model.structure,
                components: {
                    ...components,
                    [componentKey]: applyComponentFieldPatch(components[componentKey], patch) as unknown as BaseComponent,
                },
            },
        },
    };
}

function deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
    if (Array.isArray(a) !== Array.isArray(b)) return false;
    const aRecord = a as Record<string, unknown>;
    const bRecord = b as Record<string, unknown>;
    const aKeys = Object.keys(aRecord).filter(key => aRecord[key] !== undefined);
    const bKeys = Object.keys(bRecord).filter(key => bRecord[key] !== undefined);
    if (aKeys.length !== bKeys.length) return false;
    return aKeys.every(key => deepEqual(aRecord[key], bRecord[key]));
}

function updatedWhen(value: unknown): string | undefined {
    if (!value || typeof value !== 'object') return undefined;
    const when = (value as Record<string, unknown>).updatedWhen;
    return typeof when === 'string' ? when : undefined;
}

/**
 * The fields of a saved patch that the component does not show yet. A field
 * drops out once the component carries the saved value, or a ValueWithMetadata
 * stamped later than it (a newer edit, e.g. from iOS, wins over the patch).
 */
export function unconfirmedPatchFields(
    component: object,
    patch: ComponentFieldPatch
): ComponentFieldPatch {
    const current = component as Record<string, unknown>;
    const remaining: ComponentFieldPatch = {};
    for (const [field, value] of Object.entries(patch)) {
        const shown = current[field];
        if (value === null || value === undefined ? shown === undefined : deepEqual(shown, value)) continue;
        const shownWhen = updatedWhen(shown);
        const savedWhen = updatedWhen(value);
        if (shownWhen && savedWhen && shownWhen > savedWhen) continue;
        remaining[field] = value;
    }
    return remaining;
}
